<template>
  <section class="guided-mode__card guided-mode-demographics">
    <p class="guided-mode__prompt">{{ $t('app.assessment_mode.guided.demographics.instructions') }}</p>

    <div v-if="identifierLabel" class="guided-mode-demographics__group">
      <label for="guided-demographics-identifier">{{ identifierLabel }}</label>
      <input
        id="guided-demographics-identifier"
        class="guided-mode-demographics__identifier"
        type="text"
        autocomplete="off"
        :aria-invalid="String(identifierInvalid)"
        :placeholder="identifierLabel"
        :value="identifierValue"
        @input="$emit('identifier-changed', $event.target.value)"
      >
      <p v-if="identifierInvalid" class="guided-mode-demographics__field-error" role="alert">
        {{ identifierError }}
      </p>
    </div>

    <fieldset class="guided-mode-demographics__group">
      <legend>{{ agePrompt || $t('questions.age.prompt') }}</legend>
      <div class="guided-mode-demographics__age">
        <label class="guided-mode__sr-only" for="guided-demographics-age">{{ ageTitle || $t('questions.age.title') }}</label>
        <input id="guided-demographics-age" :value="age" :min="ageMinimum" :max="ageMaximum" step="1" type="number"
               :aria-invalid="String(ageInvalid)"
               :placeholder="ageTitle || $t('questions.age.title')"
               @input="$emit('age-changed', Number($event.target.value))">
        <fieldset id="guided-demographics-age-unit" class="guided-mode-demographics__age-units"
                  :aria-label="ageTitle || $t('questions.age.title')">
          <button v-for="option in ageUnitOptions" :key="option.value" type="button"
                  class="guided-mode-demographics__age-unit"
                  :class="{ 'guided-mode-demographics__age-unit--selected': ageUnit === option.value }"
                  :aria-pressed="String(ageUnit === option.value)"
                  @click="$emit('age-unit-changed', option.value)">
            {{ $t(option.labelToken) }}
          </button>
        </fieldset>
        <p v-if="ageInvalid" class="guided-mode-demographics__field-error guided-mode-demographics__age-error" role="alert">
          {{ ageError || $t('questions.age.message.error.max') }}
        </p>
      </div>
      <p v-if="ageSummary" class="guided-mode-demographics__result guided-mode-demographics__age-summary" aria-live="polite">
        {{ ageTitle || $t('questions.age.title') }}: <strong>{{ ageSummary }}</strong>
      </p>
    </fieldset>

    <fieldset class="guided-mode-demographics__group">
      <legend>{{ sexPrompt || $t('questions.gender.prompt') }}</legend>
      <div class="guided-mode-demographics__choices guided-mode-demographics__choices--gender">
        <button v-for="option in sexOptions" :key="option.value" type="button" class="guided-mode__option"
                :class="{ 'guided-mode__option--selected': sex === option.value }"
                :disabled="sexDisabled"
                @click="$emit('sex-changed', option.value)">
          <span class="guided-mode__control">{{ sex === option.value ? '●' : '' }}</span>{{ $t(option.labelToken) }}
        </button>
      </div>
    </fieldset>

    <fieldset v-if="collectMaternalStatus" class="guided-mode-demographics__group">
      <legend>{{ $t('questions.maternal_status.prompt') }}</legend>
      <div class="guided-mode-demographics__choices guided-mode-demographics__choices--maternal-status">
        <button v-for="option in maternalStatusOptions" :key="option.value" type="button" class="guided-mode__option"
                :class="{ 'guided-mode__option--selected': maternalStatus === option.value }"
                :disabled="maternalStatusDisabled"
                @click="$emit('maternal-status-changed', option.value)">
          <span class="guided-mode__control">{{ maternalStatus === option.value ? '●' : '' }}</span>{{ $t(option.labelToken) }}
        </button>
      </div>
    </fieldset>

    <p v-if="assessmentLabel" class="guided-mode-demographics__result">{{ $t('app.assessment_mode.guided.demographics.assessment_label') }} <strong>{{assessmentLabel }}</strong></p>
    <p v-if="assessmentUnavailable" class="guided-mode-demographics__error">
      {{ $t('app.assessment_unavailable.check_details') }}
    </p>
  </section>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue'
import type {GuidedAgeUnit, GuidedMaternalStatus, GuidedPatientSex} from '../types/guided-mode.types'
import {formatAgeSummary} from '../utils/age-summary'

interface SexOption {
  value: GuidedPatientSex
  labelToken: string
}

const AGE_UNIT_OPTIONS = Object.freeze([
  {value: 'days', labelToken: 'units.days.short'},
  {value: 'months', labelToken: 'units.months.short'},
  {value: 'years', labelToken: 'units.years.short'},
] as const)

const SEX_OPTIONS = Object.freeze([
  {value: 'male', labelToken: 'responses.gender.male.title'},
  {value: 'female', labelToken: 'responses.gender.female.title'},
] as const)

const MATERNAL_STATUS_OPTIONS = Object.freeze([
  {value: 'antenatal', labelToken: 'responses.maternal_status.antenatal'},
  {value: 'postnatal', labelToken: 'responses.maternal_status.postnatal'},
  {value: 'none', labelToken: 'responses.maternal_status.none'},
] as const)

export default defineComponent({
  name: 'GuidedModeDemographics',
  setup: () => ({
    ageUnitOptions: AGE_UNIT_OPTIONS,
    maternalStatusOptions: MATERNAL_STATUS_OPTIONS,
  }),
  props: {
    age: {type: Number, default: undefined},
    ageUnit: {type: String as PropType<GuidedAgeUnit>, required: true},
    ageMinimum: {type: Number, default: 1},
    ageMaximum: {type: Number, required: true},
    agePrompt: {type: String, default: ''},
    ageTitle: {type: String, default: ''},
    ageInvalid: {type: Boolean, default: false},
    ageError: {type: String, default: ''},
    sex: {type: String as PropType<GuidedPatientSex>, default: ''},
    sexPrompt: {type: String, default: ''},
    sexOptions: {type: Array as PropType<ReadonlyArray<SexOption>>, default: () => SEX_OPTIONS},
    sexDisabled: {type: Boolean, default: false},
    identifierLabel: {type: String, default: ''},
    identifierValue: {type: String, default: ''},
    identifierError: {type: String, default: ''},
    identifierInvalid: {type: Boolean, default: false},
    maternalStatus: {type: String as PropType<GuidedMaternalStatus>, default: ''},
    maternalStatusDisabled: {type: Boolean, default: false},
    collectMaternalStatus: {type: Boolean, default: false},
    assessmentLabel: {type: String, default: ''},
    assessmentUnavailable: {type: Boolean, default: false},
  },
  emits: ['age-unit-changed', 'age-changed', 'sex-changed', 'identifier-changed', 'maternal-status-changed'],
  computed: {
    ageSummary(): string {
      return formatAgeSummary(this, (value, unit) =>
        `${this.$i18n.n(value, 'decimal')} ${this.$t(`partials.time.${unit}${value === 1 ? '' : 's'}`)}`)
    },
  },
})
</script>
